import { AssessmentTestContextType, TimeInterval, SyncStatus } from '.';
import { AssessmentTestResponse } from '../../api/teams';
import dayjs from 'dayjs';

export function updateSection({
  state,
  stepId,
  sectionId,
  updater,
}: {
  state: AssessmentTestContextType;
  stepId: string;
  sectionId: string;
  updater: (section: SectionState) => SectionState;
}): AssessmentTestContextType {
  return {
    ...state,
    ...queueForSync(state),
    steps: state.steps.map((step) =>
      step.id === stepId
        ? {
            ...step,
            sections: step.sections.map((s) =>
              s.id === sectionId ? updater(s) : s
            ),
          }
        : step
    ),
  };
}

export function updateSectionOfType<K extends SectionState['type']>(
  state: AssessmentTestContextType,
  stepId: string,
  sectionId: string,
  type: K,
  updater: (
    section: Extract<SectionState, { type: K }>
  ) => Extract<SectionState, { type: K }>
): AssessmentTestContextType {
  return updateSection({
    state,
    stepId,
    sectionId,
    updater: (section) => {
      if (section.type !== type) {
        throw new Error(`Section ${sectionId} is not of type ${type}`);
      }
      return updater(section as Extract<SectionState, { type: K }>);
    },
  });
}

export type SourceSection =
  AssessmentTestResponse['steps'][number]['sections'][number];

export type SourceAnswerSection =
  AssessmentTestResponse['answers'][number]['sections'][number];

type SectionBy<K extends SourceSection['type']> = Extract<
  SourceSection,
  { type: K }
>;
type AnswerBy<K extends SourceSection['type']> = Extract<
  SourceAnswerSection,
  { type: K }
>;
export type SectionState =
  AssessmentTestContextType['steps'][number]['sections'][number];
export type VariantResult<K extends SourceSection['type']> = Extract<
  SectionState,
  { type: K }
>;

export const sectionBuilders: {
  [K in SourceSection['type']]: (
    s: SectionBy<K>,
    a?: AnswerBy<K>
  ) => Omit<
    VariantResult<K>,
    | 'id'
    | 'title'
    | 'didInteract'
    | 'isAnswered'
    | 'content'
    | 'timeIntervals'
    | 'followUpContent'
    | 'hasFollowUp'
    | 'followUpAnswer'
    | 'followUpPlaceholder'
  >;
} = {
  'text-input': (s, a) => ({
    type: 'text-input',
    answer: a?.answer ?? '',
    placeholder: s?.placeholder ?? null,
  }),
  ranking: (s, a) => ({
    type: 'ranking',
    options: s.options,
    // Initialize with default order so UI has items to render and reorder
    answer: a?.answer ?? s.options,
  }),
  'multiple-choice': (s, a) => ({
    type: 'multiple-choice',
    isSingleChoice: s.isSingleChoice,
    options: s.options,
    answer: a?.answer ?? [],
  }),
  matching: (s, a) => ({
    type: 'matching',
    leftItems: s.leftItems,
    rightItems: s.rightItems,
    answer: a?.answer ?? [],
  }),
  'fill-in-the-gap': (s, a) => ({
    type: 'fill-in-the-gap',
    blanks: s.blanks,
    answer: a?.answer ?? [],
  }),
  'text-template': (s, a) => ({
    type: 'text-template',
    blanks: s.blanks,
    answer: a?.answer ?? [],
    template: s.template,
  }),
  'py-playground': (s, a) => ({
    type: 'py-playground',
    setupCode: s.setupCode,
    startingCode: s.startingCode,
    answer: a?.answer ?? { output: '', code: s.startingCode },
  }),
  'llm-playground': (s, a) => ({
    type: 'llm-playground',
    initialPrompt: s.initialPrompt,
    answer: a?.answer ?? { prompt: s.initialPrompt, output: '' },
  }),
  'py-editor': (s, a) => ({
    type: 'py-editor',
    startingCode: s.startingCode,
    answer: a?.answer ?? s.startingCode,
  }),
  'llm-editor': (s, a) => ({
    type: 'llm-editor',
    initialPrompt: s.initialPrompt,
    answer: a?.answer ?? s.initialPrompt,
  }),
  'multiple-rating': (s, a) => ({
    type: 'multiple-rating',
    questions: s.questions,
    answer: (a?.answer ?? []).map((x) => ({
      id: x.id,
      label: x.label,
    })),
  }),
  'matrix-ranking': (s, a) => ({
    type: 'matrix-ranking',
    items: s.items,
    horizontalAxisLabel: s.horizontalAxisLabel,
    verticalAxisLabel: s.verticalAxisLabel,
    annotationPrompt: s.annotationPrompt,
    answer: a?.answer ?? [],
  }),
};

export const getSectionClassName = (stepId: string, sectionId: string) =>
  `step-${stepId}-section-${sectionId}`;

export function getStepSecondsSpent(
  step: AssessmentTestContextType['steps'][number],
  now: number = Date.now()
) {
  return (
    step.sections.reduce((acc, section) => {
      return (
        acc +
        section.timeIntervals.reduce((acc, interval) => {
          return (
            acc +
            dayjs(interval.endedAt ?? new Date(now)).diff(
              dayjs(interval.startedAt),
              'seconds'
            )
          );
        }, 0)
      );
    }, 0) +
    (step.introduction?.timeIntervals ?? []).reduce((acc, interval) => {
      return (
        acc +
        dayjs(interval.endedAt ?? new Date(now)).diff(
          dayjs(interval.startedAt),
          'seconds'
        )
      );
    }, 0)
  );
}

export const SELF_ASSESSMENT_LABEL = 'Self Assessment';
export const SELF_ASSESSMENT_STEP_ID = 'self-assessment';

export function isSelfAssessmentStep(
  step: AssessmentTestContextType['steps'][number]
) {
  // Match by id (the canonical identifier authored in the test content) and
  // keep the label check as a backstop, so neither convention can silently
  // break the self-assessment exclusion.
  return (
    step.id === SELF_ASSESSMENT_STEP_ID || step.label === SELF_ASSESSMENT_LABEL
  );
}

export function getAssessmentSecondsSpent(
  test: AssessmentTestContextType,
  now: number = Date.now()
) {
  return test.steps
    .filter((step) => !isSelfAssessmentStep(step))
    .reduce((acc, step) => {
      return acc + getStepSecondsSpent(step, now);
    }, 0);
}

export function closeOpenStepIntervals({
  step,
  timestamp,
}: {
  step: AssessmentTestContextType['steps'][number];
  timestamp: Date;
}) {
  return {
    ...step,
    ...(step.introduction
      ? {
          introduction: {
            ...step.introduction,
            timeIntervals: closeOpenIntervals({
              existingIntervals: step.introduction.timeIntervals,
              timestamp,
            }),
          },
        }
      : {}),
    sections: step.sections.map((section) => ({
      ...section,
      timeIntervals: closeOpenIntervals({
        existingIntervals: section.timeIntervals,
        timestamp,
      }),
    })),
  };
}

export function closeOpenIntervals({
  existingIntervals,
  timestamp,
}: {
  existingIntervals: TimeInterval[];
  timestamp: Date;
}) {
  return existingIntervals.map((interval) =>
    interval?.endedAt
      ? interval
      : {
          ...interval,
          endedAt: timestamp,
        }
  );
}

export function openInterval({
  existingIntervals,
  timestamp,
}: {
  existingIntervals: TimeInterval[];
  timestamp: Date;
}) {
  const last = existingIntervals[existingIntervals.length - 1];
  if (last && last.endedAt === null) {
    return existingIntervals;
  }
  return [...existingIntervals, { startedAt: timestamp, endedAt: null }];
}

export function queueForSync(state: AssessmentTestContextType) {
  return {
    sync: {
      ...state.sync,
      status:
        state.sync.status === SyncStatus.Syncing
          ? SyncStatus.QueuedWhileSyncing
          : SyncStatus.Queued,
      // Reset backoff so user activity triggers an immediate retry
      backoffAttempt: 0,
    },
  };
}

export function isSectionAnswered(section: SectionState) {
  const type = section.type;
  switch (type) {
    case 'text-input':
      return isTextInputQuestionAnswered(section);
    case 'ranking':
      return isRankingQuestionAnswered(section);
    case 'multiple-choice':
      return isMultipleChoiceQuestionAnswered(section);
    case 'matching':
      return isMatchingQuestionAnswered(section);
    case 'fill-in-the-gap':
      return isFillInTheGapQuestionAnswered(section);
    case 'text-template':
      return isTextTemplateQuestionAnswered(section);
    case 'py-playground':
      return isPythonPlaygroundQuestionAnswered(section);
    case 'llm-playground':
      return isLLMPlaygroundQuestionAnswered(section);
    case 'multiple-rating':
      return isMultipleRatingQuestionAnswered(section);
    case 'py-editor':
      return isPythonEditorQuestionAnswered(section);
    case 'llm-editor':
      return isLLMEditorQuestionAnswered(section);
    case 'matrix-ranking':
      return isMatrixRankingQuestionAnswered(section);
    default:
      return false;
  }
}

export function isTextInputQuestionAnswered(
  section: Extract<SectionState, { type: 'text-input' }>
) {
  return section.answer.length > 0 && isFollowUpAnswered(section);
}

export function isRankingQuestionAnswered(
  section: Extract<SectionState, { type: 'ranking' }>
) {
  return (
    section.answer.length === section.options.length &&
    isFollowUpAnswered(section)
  );
}

export function isMultipleChoiceQuestionAnswered(
  section: Extract<SectionState, { type: 'multiple-choice' }>
) {
  return section.answer.length > 0 && isFollowUpAnswered(section);
}

export function isMatchingQuestionAnswered(
  section: Extract<SectionState, { type: 'matching' }>
) {
  return (
    section.answer.length === section.leftItems.length &&
    isFollowUpAnswered(section)
  );
}

export function isFillInTheGapQuestionAnswered(
  section: Extract<SectionState, { type: 'fill-in-the-gap' }>
) {
  return (
    section.answer.length === section.blanks.length &&
    section.answer.every((a) => a.content.length > 0) &&
    isFollowUpAnswered(section)
  );
}

export function isTextTemplateQuestionAnswered(
  section: Extract<SectionState, { type: 'text-template' }>
) {
  return (
    section.answer.length === section.blanks.length &&
    section.answer.every((a) => a.content.length > 0) &&
    isFollowUpAnswered(section)
  );
}
export function isPythonPlaygroundQuestionAnswered(
  section: Extract<SectionState, { type: 'py-playground' }>
) {
  return section.answer.code.length > 0 && isFollowUpAnswered(section);
}

export function isPythonEditorQuestionAnswered(
  section: Extract<SectionState, { type: 'py-editor' }>
) {
  return section.answer.length > 0 && isFollowUpAnswered(section);
}

export function isLLMPlaygroundQuestionAnswered(
  section: Extract<SectionState, { type: 'llm-playground' }>
) {
  return section.answer.prompt.length > 0 && isFollowUpAnswered(section);
}

export function isLLMEditorQuestionAnswered(
  section: Extract<SectionState, { type: 'llm-editor' }>
) {
  return section.answer.length > 0 && isFollowUpAnswered(section);
}

export function isMultipleRatingQuestionAnswered(
  section: Extract<SectionState, { type: 'multiple-rating' }>
) {
  return (
    section.answer.length === section.questions.length &&
    isFollowUpAnswered(section)
  );
}

export function isFollowUpAnswered(section: SectionState) {
  if (!section?.hasFollowUp) {
    return true;
  }
  return section.followUpAnswer?.length > 0;
}

export function isMatrixRankingQuestionAnswered(
  section: Extract<SectionState, { type: 'matrix-ranking' }>
) {
  return (
    section.answer.length === section.items.length &&
    section.answer.every((a) => a.x !== null && a.y !== null) &&
    isFollowUpAnswered(section)
  );
}
